/**
 * Shared category tile data (slug + bilingual label + image variants).
 *
 * Wave A: tiles render in a 56-64 CSS px slot, so the 128px file is the 1x
 * source and the existing 256px file only serves 2x screens. Both are shipped
 * as AVIF/WebP with a JPG fallback and referenced through a 1x/2x srcSet.
 */
import engineOilAvif128 from "@/assets/categories/engine-oil-128.avif?no-inline";
import engineOilAvif256 from "@/assets/categories/engine-oil-256.avif?no-inline";
import engineOilWebp128 from "@/assets/categories/engine-oil-128.webp?no-inline";
import engineOilWebp256 from "@/assets/categories/engine-oil-256.webp?no-inline";
import engineOilJpg128 from "@/assets/categories/engine-oil-128.jpg?no-inline";
import engineOilJpg256 from "@/assets/categories/engine-oil-256.jpg?no-inline";
import brakePadsAvif128 from "@/assets/categories/brake-pads-128.avif?no-inline";
import brakePadsAvif256 from "@/assets/categories/brake-pads-256.avif?no-inline";
import brakePadsWebp128 from "@/assets/categories/brake-pads-128.webp?no-inline";
import brakePadsWebp256 from "@/assets/categories/brake-pads-256.webp?no-inline";
import brakePadsJpg128 from "@/assets/categories/brake-pads-128.jpg?no-inline";
import brakePadsJpg256 from "@/assets/categories/brake-pads-256.jpg?no-inline";
import tyreAvif128 from "@/assets/categories/tyre-128.avif?no-inline";
import tyreAvif256 from "@/assets/categories/tyre-256.avif?no-inline";
import tyreWebp128 from "@/assets/categories/tyre-128.webp?no-inline";
import tyreWebp256 from "@/assets/categories/tyre-256.webp?no-inline";
import tyreJpg128 from "@/assets/categories/tyre-128.jpg?no-inline";
import tyreJpg256 from "@/assets/categories/tyre-256.jpg?no-inline";
import batteryAvif128 from "@/assets/categories/battery-128.avif?no-inline";
import batteryAvif256 from "@/assets/categories/battery-256.avif?no-inline";
import batteryWebp128 from "@/assets/categories/battery-128.webp?no-inline";
import batteryWebp256 from "@/assets/categories/battery-256.webp?no-inline";
import batteryJpg128 from "@/assets/categories/battery-128.jpg?no-inline";
import batteryJpg256 from "@/assets/categories/battery-256.jpg?no-inline";
import airFilterAvif128 from "@/assets/categories/air-filter-128.avif?no-inline";
import airFilterAvif256 from "@/assets/categories/air-filter-256.avif?no-inline";
import airFilterWebp128 from "@/assets/categories/air-filter-128.webp?no-inline";
import airFilterWebp256 from "@/assets/categories/air-filter-256.webp?no-inline";
import airFilterJpg128 from "@/assets/categories/air-filter-128.jpg?no-inline";
import airFilterJpg256 from "@/assets/categories/air-filter-256.jpg?no-inline";
import sparkPlugAvif128 from "@/assets/categories/spark-plug-128.avif?no-inline";
import sparkPlugAvif256 from "@/assets/categories/spark-plug-256.avif?no-inline";
import sparkPlugWebp128 from "@/assets/categories/spark-plug-128.webp?no-inline";
import sparkPlugWebp256 from "@/assets/categories/spark-plug-256.webp?no-inline";
import sparkPlugJpg128 from "@/assets/categories/spark-plug-128.jpg?no-inline";
import sparkPlugJpg256 from "@/assets/categories/spark-plug-256.jpg?no-inline";
import chainSprocketAvif128 from "@/assets/categories/chain-sprocket-128.avif?no-inline";
import chainSprocketAvif256 from "@/assets/categories/chain-sprocket-256.avif?no-inline";
import chainSprocketWebp128 from "@/assets/categories/chain-sprocket-128.webp?no-inline";
import chainSprocketWebp256 from "@/assets/categories/chain-sprocket-256.webp?no-inline";
import chainSprocketJpg128 from "@/assets/categories/chain-sprocket-128.jpg?no-inline";
import chainSprocketJpg256 from "@/assets/categories/chain-sprocket-256.jpg?no-inline";
import suspensionAvif128 from "@/assets/categories/suspension-128.avif?no-inline";
import suspensionAvif256 from "@/assets/categories/suspension-256.avif?no-inline";
import suspensionWebp128 from "@/assets/categories/suspension-128.webp?no-inline";
import suspensionWebp256 from "@/assets/categories/suspension-256.webp?no-inline";
import suspensionJpg128 from "@/assets/categories/suspension-128.jpg?no-inline";
import suspensionJpg256 from "@/assets/categories/suspension-256.jpg?no-inline";

export type CategoryTile = {
  /** Stable tile id (also used as a React key and analytics label). */
  slug: string;
  /**
   * Slug of the real catalogue category this tile resolves to (`/c/{slug}`
   * and the product-count lookup). Tile ids are merchandising labels and do
   * not always match a row in `categories`, so this mapping keeps links from
   * 404-ing and keeps the count column populated.
   */
  catalogSlug: string;
  en: string;
  bn: string;
  /** 1x (128px) sources. */
  avif: string;
  webp: string;
  jpg: string;
  /** 2x (256px) sources for high-DPR screens. */
  avif2x: string;
  webp2x: string;
  jpg2x: string;
};

/** `srcSet` string pairing the 1x and 2x variant of one format. */
export function tileSrcSet(one: string, two: string): string {
  return `${one} 1x, ${two} 2x`;
}

export const CATEGORY_TILES: CategoryTile[] = [
  {
    slug: "engine-oil",
    catalogSlug: "lubricants",
    en: "Engine Oil",
    bn: "ইঞ্জিন অয়েল",
    avif: engineOilAvif128,
    webp: engineOilWebp128,
    jpg: engineOilJpg128,
    avif2x: engineOilAvif256,
    webp2x: engineOilWebp256,
    jpg2x: engineOilJpg256,
  },
  {
    slug: "brake-pads",
    catalogSlug: "brake-system",
    en: "Brake Pads",
    bn: "ব্রেক প্যাড",
    avif: brakePadsAvif128,
    webp: brakePadsWebp128,
    jpg: brakePadsJpg128,
    avif2x: brakePadsAvif256,
    webp2x: brakePadsWebp256,
    jpg2x: brakePadsJpg256,
  },
  {
    slug: "tyre",
    catalogSlug: "tyres-tubes",
    en: "Tyres",
    bn: "টায়ার",
    avif: tyreAvif128,
    webp: tyreWebp128,
    jpg: tyreJpg128,
    avif2x: tyreAvif256,
    webp2x: tyreWebp256,
    jpg2x: tyreJpg256,
  },
  {
    slug: "battery",
    catalogSlug: "batteries",
    en: "Battery",
    bn: "ব্যাটারি",
    avif: batteryAvif128,
    webp: batteryWebp128,
    jpg: batteryJpg128,
    avif2x: batteryAvif256,
    webp2x: batteryWebp256,
    jpg2x: batteryJpg256,
  },
  {
    slug: "air-filter",
    catalogSlug: "filters-oil",
    en: "Filters",
    bn: "ফিল্টার",
    avif: airFilterAvif128,
    webp: airFilterWebp128,
    jpg: airFilterJpg128,
    avif2x: airFilterAvif256,
    webp2x: airFilterWebp256,
    jpg2x: airFilterJpg256,
  },
  {
    slug: "spark-plug",
    catalogSlug: "engine-parts",
    en: "Spark Plug",
    bn: "স্পার্ক প্লাগ",
    avif: sparkPlugAvif128,
    webp: sparkPlugWebp128,
    jpg: sparkPlugJpg128,
    avif2x: sparkPlugAvif256,
    webp2x: sparkPlugWebp256,
    jpg2x: sparkPlugJpg256,
  },
  {
    slug: "chain-sprocket",
    catalogSlug: "chain-sprocket",
    en: "Chain & Sprocket",
    bn: "চেইন ও স্প্রকেট",
    avif: chainSprocketAvif128,
    webp: chainSprocketWebp128,
    jpg: chainSprocketJpg128,
    avif2x: chainSprocketAvif256,
    webp2x: chainSprocketWebp256,
    jpg2x: chainSprocketJpg256,
  },
  {
    slug: "suspension",
    catalogSlug: "suspension",
    en: "Suspension",
    bn: "সাসপেনশন",
    avif: suspensionAvif128,
    webp: suspensionWebp128,
    jpg: suspensionJpg128,
    avif2x: suspensionAvif256,
    webp2x: suspensionWebp256,
    jpg2x: suspensionJpg256,
  },
];

export default CATEGORY_TILES;
